import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { FiLock, FiUser, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/common/Button';
import BrandLogo from '../../components/common/BrandLogo';
import { getSiteMode, getLoginSubtitle } from '../../utils/site';

// 公开品牌信息(无需登录): 登录页贴牌展示, 不走 SettingsContext(未认证不启用)
const fetchPublicSettings = async () => {
  const response = await fetch('/api/settings/public').then(r => r.json()).catch(() => ({}));
  return response.settings || {};
};
const fetchBackground = async () => {
  const response = await fetch('/api/settings/background').then(r => r.json()).catch(() => ({}));
  return response.url || '';
};

const Login = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 贴牌信息: 品牌标题优先于平台标题; 背景图有则渲染
  const { data: publicSettings } = useQuery('publicSettings', fetchPublicSettings, {
    staleTime: 5 * 60 * 1000,
    retry: false
  });
  const { data: backgroundUrl } = useQuery('settingsBackground', fetchBackground, {
    staleTime: 5 * 60 * 1000,
    retry: false
  });
  const systemTitle = publicSettings?.brandTitle || publicSettings?.systemTitle || '运维总控';
  const systemLogo = publicSettings?.systemLogo || '';
  // 站点模式: taishan=超管后台 / daili=代理后台(kehu 无页面, Nginx 层已挡)
  const siteMode = getSiteMode();
  
  // 状态
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // 按菜单权限决定登录后落地页: 无来源页时, 有总览权限进总览, 否则进个人资料
  const landingFor = (user, fromPath) => {
    if (fromPath) return fromPath;
    if (!user) return '/dashboard';
    const isAdmin = user.role === 'admin';
    const perms = Array.isArray(user.menuPermissions) ? user.menuPermissions : [];
    return (isAdmin || perms.includes('dashboard')) ? '/dashboard' : '/profile';
  };

  // 如果已登录，重定向到首页或之前的页面
  useEffect(() => {
    if (isAuthenticated) {
      navigate(landingFor(null, location.state?.from?.pathname), { replace: true });
    }
  }, [isAuthenticated, navigate, location]);
  
  // 处理登录
  const handleLogin = async (e) => {
    e.preventDefault();
    
    // 表单验证
    if (!username.trim() || !password.trim()) {
      setError('用户名和密码不能为空');
      return;
    }
    
    setError('');
    setIsLoading(true);
    
    try {
      const user = await login(username, password);
      
      if (user) {
        navigate(landingFor(user, location.state?.from?.pathname), { replace: true });
      }
    } catch (error) {
      setError('登录失败，请检查用户名和密码');
    } finally {
      setIsLoading(false);
    }
  };
  
  // 页面动画
  const pageVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  
  return (
    <motion.div
      variants={pageVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen flex items-center justify-center bg-[#f6f8fa] px-4 py-12"
      style={backgroundUrl ? {
        backgroundImage: `url(${backgroundUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      } : undefined}
    >
      <div className="relative z-10 w-full max-w-[400px]">
        {/* 品牌标识 */}
        <motion.div variants={itemVariants} className="flex flex-col items-center mb-8">
          {systemLogo ? (
            <img src={systemLogo} alt="logo" className="h-14 w-14 rounded object-contain mb-3" />
          ) : (
            <span className="mb-3"><BrandLogo size={56} /></span>
          )}
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-2xl font-semibold text-[#1f2328]"
          >
            {systemTitle}
          </motion.h1>
          <p className="mt-1 text-sm text-[#656d76]">{getLoginSubtitle(siteMode)}</p>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="bg-white border border-[#d0d7de] rounded-md shadow-sm p-6"
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-[#ffebe9] border border-[#ffcecb] rounded-md p-3 mb-4 flex items-center gap-3 text-[#cf222e] text-sm"
            >
              <FiAlertCircle size={18} className="shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}
          
          <form onSubmit={handleLogin}>
            <motion.div variants={itemVariants} className="mb-4">
              <label className="block text-[#24292f] text-sm font-medium mb-1.5">
                用户名
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8b949e]">
                  <FiUser />
                </span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="输入用户名"
                  className="w-full py-2 px-3 pl-9 bg-white rounded-md text-[#1f2328] border border-[#d0d7de] focus:outline-none focus:border-[#0969da] focus:shadow-[0_0_0_3px_rgba(9,105,218,0.3)]"
                />
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="mb-6">
              <label className="block text-[#24292f] text-sm font-medium mb-1.5">
                密码
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8b949e]">
                  <FiLock />
                </span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="输入密码"
                  className="w-full py-2 px-3 pl-9 bg-white rounded-md text-[#1f2328] border border-[#d0d7de] focus:outline-none focus:border-[#0969da] focus:shadow-[0_0_0_3px_rgba(9,105,218,0.3)]"
                />
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Button
                type="submit"
                variant="primary"
                fullWidth={true}
                disabled={isLoading}
              >
                {isLoading ? '登录中...' : '登录'}
              </Button>
            </motion.div>
          </form>
          
          <motion.div
            variants={itemVariants}
            className="mt-5 pt-4 border-t border-[#d0d7de] text-center text-sm text-[#57606a]"
          >
            还没有账户？ <Link to="/register" className="text-[#0969da] hover:underline">注册新账户</Link>
          </motion.div>
        </motion.div>
        
        <motion.div
          variants={itemVariants}
          className="mt-6 text-center text-[#8b949e] text-xs"
        >
          © {new Date().getFullYear()} {systemTitle}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Login;
